import { useCallback, useMemo } from "react";
import { IHttpClient } from "../../../lib/http";
import { regenPropertyCache } from "../../common/api";
import { DisplayEntity } from "../types";
import { reorderList } from "../utils/reorderList";

export function useDisplayApi(httpClient: IHttpClient, propertyTicker: string) {
  // GET all
  const getAll = useCallback(async (): Promise<DisplayEntity[]> => {
    return await httpClient.get<DisplayEntity[]>(`/api/properties/${propertyTicker}/displays`, {
      headers: { "x-property": propertyTicker },
    });
  }, [httpClient, propertyTicker]);

  // GET one
  const getOne = useCallback(
    async (id: string): Promise<DisplayEntity> => {
      return await httpClient.get<DisplayEntity>(
        `/api/properties/${propertyTicker}/displays/${id}`,
        { headers: { "x-property": propertyTicker } }
      );
    },
    [httpClient, propertyTicker]
  );

  // CREATE
  const create = useCallback(
    async (entity: DisplayEntity): Promise<DisplayEntity> => {
      const data = new FormData();

      // Upload new image files from imageManager (must be File objects)
      if (entity.multimedia) {
        entity.multimedia.forEach(file => {
          const isNewFile = file.url && file.url.startsWith("blob:");
          if (isNewFile && file.file) {
            data.append("images", file.file, file.filename);
          }
        });
      }

      // Only send metadata in JSON payload
      const cleanEntity = {
        ...entity,
        multimedia: entity.multimedia?.map(multimedia => ({
          id: multimedia.id,
          filename: multimedia.filename,
          url: multimedia.url,
        })),
        imageManager: undefined,
        originalMultimedia: undefined,
      };

      data.append("entity", new Blob([JSON.stringify(cleanEntity)], { type: "application/json" }));

      const resp = await httpClient.post<FormData, DisplayEntity>(
        `/api/properties/${propertyTicker}/displays`,
        data,
        { headers: { "Content-Type": "multipart/form-data", "x-property": propertyTicker } }
      );
      await regenPropertyCache(httpClient, propertyTicker);

      return resp;
    },
    [httpClient, propertyTicker]
  );

  // UPDATE
  const update = useCallback(
    async (id: string, entity: DisplayEntity): Promise<DisplayEntity> => {
      const data = new FormData();

      // Upload new image files from imageManager (must be File objects)
      if (entity.multimedia) {
        entity.multimedia.forEach(file => {
          const isNewFile = file.url && file.url.startsWith("blob:");
          if (isNewFile && file.file) {
            data.append("images", file.file, file.filename);
          }
        });
      }

      // Only send metadata in JSON payload
      const cleanEntity = {
        ...entity,
        multimedia: entity.multimedia?.map(multimedia => ({
          id: multimedia.id,
          filename: multimedia.filename,
          url: multimedia.url,
        })),
        imageManager: undefined,
        originalMultimedia: undefined,
        travelWindow: undefined,
      };
      data.append("entity", new Blob([JSON.stringify(cleanEntity)], { type: "application/json" }));

      const resp = await httpClient.put<FormData, DisplayEntity>(
        `/api/properties/${propertyTicker}/displays`,
        data,
        { headers: { "Content-Type": "multipart/form-data", "x-property": propertyTicker } }
      );
      await regenPropertyCache(httpClient, propertyTicker);

      return resp;
    },
    [httpClient, propertyTicker]
  );

  // UPDATE without cache (for reorder)
  const updateNoCache = useCallback(
    async (id: string, entity: DisplayEntity): Promise<void> => {
      const data = new FormData();

      const clean = { ...entity };
      delete clean.imageManager;
      delete clean.originalMultimedia;

      data.append("entity", new Blob([JSON.stringify(clean)], { type: "application/json" }));

      await httpClient.put<FormData, DisplayEntity>(
        `/api/properties/${propertyTicker}/displays`,
        data,
        { headers: { "Content-Type": "multipart/form-data", "x-property": propertyTicker } }
      );
    },
    [httpClient, propertyTicker]
  );

  // DELETE
  const remove = useCallback(
    async (id: string): Promise<void> => {
      await httpClient.delete<void>(`/api/properties/${propertyTicker}/displays/${id}`, {
        headers: { "x-property": propertyTicker },
      });
      await regenPropertyCache(httpClient, propertyTicker);
    },
    [httpClient, propertyTicker]
  );

  // REORDER (only fallback, without endpoint)
  const reorder = useCallback(
    async (id: string, afterId: string | undefined): Promise<void> => {
      const list = (await getAll()).filter(
        (item): item is DisplayEntity & { id: string } => typeof item.id === "string"
      );
      const sorted = reorderList(list, id, afterId);

      const changes: DisplayEntity[] = [];
      sorted.forEach((item, idx) => {
        const newPos = idx;
        if (item.position !== newPos) {
          changes.push({ ...item, position: newPos });
        }
      });

      for (const ent of changes) {
        if (ent.id) {
          await updateNoCache(ent.id, ent);
        }
      }

      await regenPropertyCache(httpClient, propertyTicker);
    },
    [getAll, updateNoCache, httpClient, propertyTicker]
  );

  return useMemo(() => ({ getAll, getOne, create, update, remove, reorder }), [
    getAll,
    getOne,
    create,
    update,
    remove,
    reorder,
  ]);
}
